/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';

import { prepareHeaders, retryFetch } from '@app/app/lib/api';

import { serverPath } from '@app/utils/server-path';

import { FetchError, handleFetchError } from '@app/app/lib';

import { CustomElement } from '@app/app/lib/slate';

import { Slide, Stage } from '../types';

import { checkSlidesAnalytics } from '../components/canvas-slide-presenter/lib/use-slides-generation-analytics';
import { areCanvasConfigsEqual } from '../utils/canvas-config-comparison';

type DownloadStatus = 'idle' | 'loading' | 'success' | 'error';

type SlideStoreState = {
  slides: Slide[];
  initialSlides: number[];
  font: string;
  downloadStatus: DownloadStatus;
  downloadError: string | null;
  palette: string[];
  imageDataSource: string;
  workspaceDescription: string;
  isActiveBackgroundImageChange: boolean;
  themeId: number | null;
  isGenerationFirstSlideDone: boolean;
  isGenerationDonePushed: boolean;
  // Ожидаем первый слайд после перехода на страницу (LLM ещё генерирует) → показываем лоадер
  isAwaitingFirstSlide: boolean;
  creationMethod: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates';
  userColor?: string;
  editorDrafts: Record<string, CustomElement[]>;
};

type SlideStoreActions = {
  setSlides: (slides: Slide[]) => void;
  setFont: (newFont: string) => void;
  updateSlideStage: (
    slideId: string,
    newStages: Stage[],
    from: string,
    workspaceId?: string,
  ) => void;
  setConfig: (slideId: string, config: any) => void;
  addEditableSlide: (id: number, position: number) => void;
  addSlide: (id: number, position: number, stages?: Stage[]) => void;
  fetchSlide: (
    workspaceId: string,
    slideId: string,
    isNeedRegenerateThumbnail?: boolean,
  ) => Promise<void>;
  clearStore: () => void;
  removeSlide: (slideId: string) => Slide | null;
  setSlideStageComposeSuccess: (slideId: string) => void;
  setDownloadStatus: (status: DownloadStatus, error?: string | null) => void;
  startDownload: () => void;
  finishDownload: (isSuccess: boolean, error?: string) => void;
  resetDownloadStatus: () => void;
  setPalette: (colors: string[]) => void;
  addColorToPalette: (color: string) => void;
  removeColorFromPalette: (color: string) => void;
  clearPalette: () => void;
  updateColorInPalette: (oldColor: string, newColor: string) => void;
  setImageDataSource: (dataSource: string) => void;
  setWorkspaceDescription: (description: string) => void;
  setIsActiveBackgroundImageChange: (isActive: boolean) => void;
  setIsRelayoutRunning: (slideId: string, isRunning: boolean) => void;
  setIsRestylingRunning: (slideId: string, isRunning: boolean) => void;
  setIsRepaintRunning: (slideId: string, isRunning: boolean) => void;
  setIsNeedRegenerateThumbnail: (slideId: string, isNeedRegenerate: boolean) => void;
  setThemeId: (newThemeId: number) => void;
  setGenerationFirstSlideDone: (value: boolean) => void;
  resetGenerationFirstSlideDone: () => void;
  markCurrentSlidesAsInitial: () => void;
  setIsAwaitingFirstSlide: (value: boolean) => void;
  setGenerationDonePushed: (value: boolean) => void;
  resetGenerationDonePushed: () => void;
  setCreationMethod: (method: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates') => void;
  setUserColor: (userColor: string) => void;
  resetUserColor: () => void;
  setEditorDraft: (slideId: string, tree: CustomElement[]) => void;
  clearEditorDraft: (slideId: string) => void;
};

type SlideStore = SlideStoreState & SlideStoreActions;

const defaultState: SlideStoreState = {
  slides: [],
  initialSlides: [],
  font: '',
  downloadStatus: 'idle',
  downloadError: null,
  palette: [],
  imageDataSource: 'default',
  workspaceDescription: '',
  isActiveBackgroundImageChange: false,
  themeId: null,
  isGenerationFirstSlideDone: false,
  isGenerationDonePushed: false,
  isAwaitingFirstSlide: false,
  creationMethod: 'prompt',
  userColor: '', // Пользовательский цвет
  editorDrafts: {},
};

export const useSlideStore = create<SlideStore>((set, get) => ({
  ...defaultState,

  markCurrentSlidesAsInitial: () =>
    set((state) => ({
      initialSlides: state.slides.map((slide) => slide.id),
    })),

  addSlide: (id, position, stages) =>
    set((state) => ({
      slides: [
        ...state.slides.map((slide) =>
          slide.position >= position ? { ...slide, position: slide.position + 1 } : slide,
        ),
        {
          id,
          position,
          stages: stages ? [...stages] : ([] as Stage[]),
          has_missing_items: false,
          error: null,
        },
      ].sort((a, b) => a.position - b.position),
      // Первый слайд пришёл — лоадер ожидания больше не нужен
      isAwaitingFirstSlide: false,
    })),

  setIsAwaitingFirstSlide: (value) => set({ isAwaitingFirstSlide: value }),

  setIsRelayoutRunning: (slideId, isRunning) =>
    set((state) => ({
      slides: state.slides.map((slide) =>
        slide.id.toString() === slideId ? { ...slide, is_relayout_running: isRunning } : slide,
      ),
    })),

  setIsRestylingRunning: (slideId, isRunning) =>
    set((state) => ({
      slides: state.slides.map((slide) =>
        slide.id.toString() === slideId ? { ...slide, is_restyling_running: isRunning } : slide,
      ),
    })),

  setIsRepaintRunning: (slideId, isRunning) =>
    set((state) => ({
      slides: state.slides.map((slide) =>
        slide.id.toString() === slideId ? { ...slide, is_repaint_running: isRunning } : slide,
      ),
    })),

  setIsNeedRegenerateThumbnail: (slideId, isNeedRegenerate) =>
    set((state) => ({
      slides: state.slides.map((slide) =>
        slide.id.toString() === slideId
          ? { ...slide, is_need_regenerate_thumbnail: isNeedRegenerate }
          : slide,
      ),
    })),

  setFont: (newFont: string) => set((state) => ({ ...state, font: newFont })),

  addEditableSlide: (id, position) =>
    set((state) => ({
      slides: [
        ...state.slides.map((slide) =>
          slide.position >= position ? { ...slide, position: slide.position + 1 } : slide,
        ),
        {
          id,
          position,
          stages: [{ type: 'editableSlide', status: 'success' }] as Stage[],
          has_missing_items: false,
          error: null,
        },
      ].sort((a, b) => a.position - b.position),
    })),

  setSlides: (slides) => set({ slides }),

  updateSlideStage: (slideId, newStages, from, workspaceId) =>
    set((state) => {
      // 🔍 ДИАГНОСТИКА
      const currentSlide = state.slides.find((s) => s.id.toString() === slideId);
      console.warn('🔍 [SlideStore.updateSlideStage] Called:', {
        slideId,
        from,
        newStages: newStages.map((s) => `${s.type}:${s.status}`).join(', '),
        currentStages:
          currentSlide?.stages?.map((s) => `${s.type}:${s.status}`).join(', ') || 'none',
        hasConfig: !!currentSlide?.config,
      });

      // Если в newStages есть is_last, то отмечаем слайды как initial
      if (newStages.some((stage) => stage.is_last)) {
        state.markCurrentSlidesAsInitial();
      }
      const updatedSlides = state.slides.map((slide) =>
        slide.id.toString() === slideId ? { ...slide, stages: [...newStages] } : slide,
      );
      let isFirstDone = state.isGenerationFirstSlideDone;
      let isDonePushed = state.isGenerationDonePushed;
      if (workspaceId) {
        try {
          const pushed = checkSlidesAnalytics(
            updatedSlides,
            workspaceId,
            from,
            isFirstDone,
            state.initialSlides,
            isDonePushed,
          );
          if (pushed === true) isFirstDone = true;
          if (pushed === 'done') isDonePushed = true;
        } catch (error) {
          console.error('❌ [Store] Analytics error:', error);
        }
      }
      return {
        slides: updatedSlides,
        isGenerationFirstSlideDone: isFirstDone,
        isGenerationDonePushed: isDonePushed,
      };
    }),

  setSlideStageComposeSuccess: (slideId) => {
    set((state) => {
      const updatedSlides = state.slides.map((slide) =>
        slide.id.toString() === slideId ? { ...slide, stages: [] } : slide,
      );
      return { slides: updatedSlides };
    });
  },

  setConfig: (slideId, config) =>
    set((state) => ({
      slides: state.slides.map((slide) =>
        slide.id.toString() === slideId ? { ...slide, config } : slide,
      ),
    })),

  setCreationMethod: (
    method: 'prompt' | 'pdf' | 'docx' | 'pptx' | 'blank' | 'templates' | undefined = 'prompt',
  ) => set({ creationMethod: method }),

  fetchSlide: async (workspaceId, slideId, isNeedRegenerateThumbnail = false) => {
    // 🔍 ДИАГНОСТИКА
    console.warn('🔍 [SlideStore.fetchSlide] Called:', {
      workspaceId,
      slideId,
      isNeedRegenerateThumbnail,
      callerStack: new Error().stack?.split('\n').slice(1, 5).join('\n'),
    });

    // TODO: fix url
    const url = serverPath.workspace.getSlideConfig(workspaceId, slideId);
    try {
      const config = await retryFetch({
        cb: async () => {
          const response = await fetch(url, {
            method: 'GET',
            headers: prepareHeaders(),
          });

          // Проверяем статус ответа, чтобы обработать 503 и другие ошибки
          if (!response.ok) {
            // Создаем объект ошибки с информацией о response для обработки в retryFetch
            const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
            (error as any).response = { status: response.status, statusText: response.statusText };
            throw error;
          }

          return await response.json();
        },
        retries: 3,
        delayBetween: 1000,
      });

      // 🛡️ ЗАЩИТА ОТ БЕСКОНЕЧНЫХ ПЕРЕРИСОВОК
      // Проверяем, отличается ли пришедший конфиг от того, что уже есть в сторе.
      // Используем нормализацию, чтобы игнорировать новые ID и временные ссылки на картинки.
      const currentSlide = get().slides.find((s) => s.id.toString() === slideId.toString());
      let configResult = config.result;

      // ПРИМЕЧАНИЕ: Защита editable/failed слайдов убрана из fetchSlide
      // fetchSlide вызывается только после compose:success, и если compose:success пришёл
      // для editable/failed слайда - это результат генерации из редактора, конфиг НУЖНО загрузить.
      // Защита от некорректных SSE событий теперь в handleSlideStageChange.

      // 🛡️ ЗАЩИТА RESTYLING СЛАЙДОВ
      // Если слайд в процессе рестайлинга (restyling:running/created), НЕ перезаписываем его stages
      // Это предотвращает ситуацию, когда асинхронный fetchSlide для compose:success
      // перезаписывает синхронно установленную стадию restyling:running
      // Важно: проверяем как стадию, так и флаг is_restyling_running
      const restylingStages = currentSlide?.stages?.filter((s) => s.type === 'restyling') || [];
      const isCurrentlyRestyling =
        restylingStages.some((s) => s.status !== 'success' && s.status !== 'fail') ||
        (currentSlide?.is_restyling_running && restylingStages.length === 0);

      if (isCurrentlyRestyling) {
        console.warn(
          '🛡️ [SlideStore.fetchSlide] BLOCKED: Slide is restyling, not overwriting stages:',
          {
            slideId,
            currentStages: currentSlide?.stages?.map((s) => `${s.type}:${s.status}`).join(', '),
            isRestylingRunningFlag: currentSlide?.is_restyling_running,
          },
        );
        // Не обновляем слайд - рестайлинг в процессе
        return;
      }

      if (currentSlide?.config && config.result) {
        if (
          currentSlide.config.content_revision === config.result.content_revision &&
          areCanvasConfigsEqual(currentSlide.config, config.result)
        ) {
          // console.debug('🛡️ [Store] Config is identical, using existing config object to prevent re-render:', { slideId });
          // Используем СТАРЫЙ объект конфига, чтобы React.memo / useEffect не триггерили изменения
          configResult = currentSlide.config;
        }
      }

      set((state) => {
        const remainingDrafts = Object.fromEntries(
          Object.entries(state.editorDrafts).filter(([key]) => key !== slideId.toString()),
        );
        return {
          slides: state.slides.map((slide) =>
            slide.id.toString() === slideId.toString()
              ? {
                  ...slide,
                  config: configResult, // Используем умный выбор конфига
                  stages: [{ type: 'compose', status: 'success' }],
                  is_relayout_running: false,
                  is_restyling_running: false,
                  is_repaint_running: false,
                  is_need_regenerate_thumbnail: isNeedRegenerateThumbnail,
                  recommended_media: config.result?.debug?.recommended_media,
                }
              : slide,
          ),
          editorDrafts: remainingDrafts,
        };
      });
      // Если слайд последний, то запоминаем структуру текущих слайдов как initial (нужно для аналитики, чтобы понимать, когда изначальные слайды были созданы)
      const isInitial = config.result?.is_last ?? false;
      if (isInitial) {
        set((state) => ({
          initialSlides: state.slides.map((slide) => slide.id),
        }));
      }
    } catch (error) {
      set((state) => ({
        slides: state.slides.map((slide) =>
          slide.id.toString() === slideId
            ? {
                ...slide,
                stages: [{ type: 'editableSlide', status: 'success' }], // Add editable slide stage if config is not loaded
                is_relayout_running: false,
                is_restyling_running: false,
                is_repaint_running: false,
                is_need_regenerate_thumbnail: false,
              }
            : slide,
        ),
      }));
      const message = `Error load config for ${slideId}: ${error}`;
      console.error(message);
      handleFetchError(new FetchError(message, url), workspaceId);
    }
  },

  removeSlide: (slideId: string): Slide | null => {
    let closestSlide: Slide | null = null;

    set((state) => {
      const slideIndex = state.slides.findIndex((slide) => slide.id.toString() === slideId);
      if (slideIndex === -1) return { slides: state.slides }; // If the slide is not found, do nothing

      const updatedSlides = state.slides
        .filter((slide) => slide.id.toString() !== slideId) // Remove the slide
        .sort((a, b) => a.position - b.position) // Sort by position
        .map((slide, index) => ({ ...slide, position: index })); // Reset positions

      // Find the closest slide to the deleted one
      closestSlide =
        updatedSlides[slideIndex - 1] || // Previous slide (it has shifted)
        updatedSlides[slideIndex] || // Next slide (it has shifted)
        null; // If there is no slide

      const remainingDrafts = Object.fromEntries(
        Object.entries(state.editorDrafts).filter(([key]) => key !== slideId),
      );
      return { slides: updatedSlides, editorDrafts: remainingDrafts };
    });

    return closestSlide;
  },

  setDownloadStatus: (status, error = null) =>
    set({ downloadStatus: status, downloadError: error }),

  startDownload: () => set({ downloadStatus: 'loading', downloadError: null }),

  finishDownload: (isSuccess, error = '') =>
    set({
      downloadStatus: isSuccess ? 'success' : 'error',
      downloadError: isSuccess ? null : error,
    }),

  resetDownloadStatus: () => set({ downloadStatus: 'idle', downloadError: null }),

  clearStore: () => set({ ...defaultState }),

  setPalette: (colors) => set({ palette: colors }),

  addColorToPalette: (color) =>
    set((state) => {
      if (!state.palette.includes(color)) {
        return { palette: [...state.palette, color] };
      }
      return state;
    }),

  removeColorFromPalette: (color) =>
    set((state) => ({
      palette: state.palette.filter((c) => c !== color),
    })),

  clearPalette: () => set({ palette: [] }),

  updateColorInPalette: (oldColor, newColor) =>
    set((state) => ({
      palette: state.palette.map((color) => (color === oldColor ? newColor : color)),
    })),

  setIsActiveBackgroundImageChange: (isActive) => set({ isActiveBackgroundImageChange: isActive }),

  setImageDataSource: (dataSource) => set({ imageDataSource: dataSource }),

  setWorkspaceDescription: (workspaceDescription: string) => set({ workspaceDescription }),
  setThemeId: (newThemeId) => set({ themeId: newThemeId }),
  setGenerationFirstSlideDone: (value: boolean) => set({ isGenerationFirstSlideDone: value }),
  resetGenerationFirstSlideDone: () => set({ isGenerationFirstSlideDone: false }),
  setGenerationDonePushed: (value: boolean) => set({ isGenerationDonePushed: value }),
  resetGenerationDonePushed: () => set({ isGenerationDonePushed: false }),

  setUserColor: (userColor: string) => set({ userColor }),
  resetUserColor: () => set({ userColor: '' }),

  setEditorDraft: (slideId, tree) =>
    set((state) => ({
      editorDrafts: { ...state.editorDrafts, [slideId]: tree },
    })),

  clearEditorDraft: (slideId) =>
    set((state) => ({
      editorDrafts: Object.fromEntries(
        Object.entries(state.editorDrafts).filter(([key]) => key !== slideId),
      ),
    })),
}));
